import { isPlainObject as isJsonObject } from 'config-hub-server/src/common/isPlainObject.js';
import { isIsoDate, isoOfDate } from './isoDate.js';

/** Lenient readers for a stored master base — an adopted record may hold any shape the backoffice ever wrote. A `Date` is not a plain object: `entityDiff.ts`'s walk needs to tell the two apart. */
export const isPlainObject = (v: unknown): v is Record<string, unknown> => isJsonObject(v) && !(v instanceof Date);

export const objectOf = (v: unknown): Record<string, unknown> => (isPlainObject(v) ? v : {});

export const asString = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

export const asNumberOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
};

export const asNumberList = (v: unknown): number[] =>
    Array.isArray(v) ? v.map(asNumberOrNull).filter((n): n is number => n !== null) : [];

export const asStringList = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];

/** `Date` or an ISO string → `YYYY-MM-DD`; anything else `''`. */
export const isoDateOf = (v: unknown): string => {
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? '' : isoOfDate(v);
    if (typeof v !== 'string' || v === '') return '';
    if (isIsoDate(v)) return v;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : isoOfDate(d);
};

export const jsonText = (v: unknown): string => (v === null || v === undefined ? '' : JSON.stringify(v, null, 2));

/** Both editors' launch type: `default` client-side, `bundle`/`widget` embedded elsewhere. */
export const LAUNCH_TYPES = ['default', 'bundle', 'widget'] as const;
export type LaunchType = (typeof LAUNCH_TYPES)[number];

/** A studio's and a game's id rule: no path separator, so a key built by joining them can always be split back. */
export const NO_SLASH = (v: string) => !v.includes('/');

/** An enum column that may hold anything on a legacy record: `value` itself when `list` has it, `fallback` otherwise. */
export const oneOf = <T extends string>(list: readonly T[], value: unknown, fallback: T): T =>
    (list as readonly string[]).includes(String(value)) ? (value as T) : fallback;

/** Ascending, without mutating `values` — a numeric-tags field's stored order. */
export const sortedNumbers = (values: readonly number[]): number[] => [...values].sort((a, b) => a - b);

/** `''` → null; otherwise `JSON.parse`, throwing on invalid JSON (the form schema reports it first). */
export const parseJsonOrNull = (text: string): unknown => {
    const trimmed = text.trim();
    return trimmed === '' ? null : JSON.parse(trimmed);
};

/** Total: the parsed value, or `fallback` when the text is not (yet) valid JSON — a form mid-keystroke must never throw. */
export const parseJsonOr = (text: string, fallback: unknown): unknown => {
    try {
        return parseJsonOrNull(text);
    } catch {
        return fallback;
    }
};

export const isJsonTextOrEmpty = (text: string): boolean => {
    try {
        parseJsonOrNull(text);
        return true;
    } catch {
        return false;
    }
};

export const nullIfEmpty = (text: string): string | null => (text.trim() === '' ? null : text.trim());
